import * as T from 'frontend/@types';
import { combineReducers } from 'redux';
import { getDirName, updatePathRoot } from 'frontend/utils';
import { FilesIndex } from 'frontend/logic/files-index';
import type { ImportQualitySettings } from 'shared';
import type { FileStoreCache } from 'frontend/logic/file-store';
import type { WorkerClient } from 'frontend/worker/client';
import { toFileStoreName } from 'frontend/logic/app-logic';
import { persistedState } from 'frontend/logic/persisted-state';
import { languageCoachReducer } from './language-coach';
import { musicReducer } from './music';

function dropboxOauth(
  state: T.DropboxOauth | null = persistedState.dropboxOauth.read(),
  action: T.Action,
): T.DropboxOauth | null {
  switch (action.type) {
    case 'set-dropbox-oauth':
      return action.oauth;
    case 'remove-dropbox-oauth':
    case 'remove-all-storage':
      return null;
    default:
      return state;
  }
}

export function serverId(
  state: string | null = persistedState.fileStoreServer.read(),
  action: T.Action,
): string | null {
  switch (action.type) {
    case 'view-list-files':
    case 'change-file-system': {
      const { fileStoreServer } = action;
      if (fileStoreServer) {
        persistedState.fileStoreServer.write(fileStoreServer.id);
      } else {
        persistedState.fileStoreServer.remove();
      }
      return action.fileStoreServer?.id ?? null;
    }
    case 'view-music': {
      persistedState.fileStoreServer.write(action.fileStoreServer.id);
      return action.fileStoreServer.id;
    }
    case 'remove-all-storage':
      return null;
    default:
      return state;
  }
}

function servers(
  state: T.FileStoreServer[] = persistedState.fileStoreServers.read(),
  action: T.Action,
): T.FileStoreServer[] {
  switch (action.type) {
    case 'add-server': {
      const { server } = action;
      const servers = [...state, server];
      persistedState.fileStoreServers.write(servers);
      return servers;
    }
    case 'remove-server': {
      const servers = state.filter(
        (server) =>
          server.url !== action.server.url &&
          server.name !== action.server.name,
      );
      persistedState.fileStoreServers.write(servers);
      return servers;
    }
    case 'update-server': {
      // Ensure the ordering of the updated server stays the same.
      const servers: T.FileStoreServer[] = [];
      const { oldServer, newServer } = action;
      for (const server of state) {
        if (server.name === oldServer.name && server.url === oldServer.url) {
          servers.push(newServer);
        } else {
          servers.push(server);
        }
      }
      persistedState.fileStoreServers.write(servers);
      return servers;
    }
    case 'remove-all-storage':
      return [];
    default:
      return state;
  }
}

function listFileErrors(
  state: Map<string, string> = new Map(),
  action: T.Action,
): Map<string, string> {
  switch (action.type) {
    case 'list-files-received': {
      const { path } = action;
      if (state.has(path)) {
        const newState = new Map(state);
        newState.delete(path);
        return newState;
      }
      return state;
    }
    case 'list-files-error': {
      const { error, path } = action;
      const newState = new Map(state);
      newState.set(path, error);
      return newState;
    }
    case 'change-file-system':
    case 'remove-dropbox-oauth':
    case 'remove-browser-files':
    case 'remove-all-storage':
      return new Map();
    default:
      return state;
  }
}

function listFilesCache(
  state: T.ListFilesCache = new Map(),
  action: T.Action,
): T.ListFilesCache {
  switch (action.type) {
    case 'move-file-done': {
      const { oldPath, metadata } = action;

      if (metadata.type === 'folder') {
        const newState: T.ListFilesCache = new Map();
        for (const [path, { isCache, files }] of state) {
          let key = path;
          if (path === oldPath || path.startsWith(oldPath + '/')) {
            key = updatePathRoot(path, oldPath, metadata.path);
          }
          newState.set(key, {
            isCache,
            files: files.map((file) => {
              if (file.path === oldPath) {
                return metadata;
              }
              if (file.path.startsWith(oldPath + '/')) {
                return {
                  ...file,
                  path: updatePathRoot(file.path, oldPath, metadata.path),
                };
              }
              return file;
            }),
          });
        }
        return newState;
      }

      // This is a file.
      const newState: T.ListFilesCache = new Map(state);
      const folder = getDirName(metadata.path);
      const entry = newState.get(folder);
      if (entry) {
        for (let i = 0; i < entry.files.length; i++) {
          const otherMetadata = entry.files[i];
          if (otherMetadata.path === oldPath) {
            const newFiles = entry.files.slice();
            newFiles[i] = metadata;
            newState.set(folder, { ...entry, files: newFiles });
            return newState;
          }
        }
      }

      return state;
    }
    case 'delete-file-done': {
      const { metadata } = action;
      const newState: T.ListFilesCache = new Map(state);

      const containingFolder = getDirName(metadata.path);
      const listing = state.get(containingFolder);
      if (listing) {
        // Filter out this folder or file.
        newState.set(containingFolder, {
          ...listing,
          files: listing.files.filter((file) => file.path !== metadata.path),
        });
      }

      if (metadata.type === 'folder') {
        newState.delete(metadata.path);
        for (const [path] of state) {
          if (path.startsWith(metadata.path + '/')) {
            newState.delete(path);
          }
        }
      }

      return newState;
    }
    case 'list-files-received': {
      const { path, files, isCache } = action;
      const newState = new Map(state);
      newState.set(path, { files, isCache });
      return newState;
    }
    case 'change-file-system':
    case 'clear-api-cache':
    case 'remove-dropbox-oauth':
    case 'remove-browser-files':
    case 'remove-all-storage':
      return new Map();
    case 'invalidate-path':
      if (state.has(action.path)) {
        const newState = new Map(state);
        newState.delete(action.path);
        return newState;
      }
      return state;
    default:
      return state;
  }
}

function downloadFileErrors(
  state: Map<string, string> = new Map(),
  action: T.Action,
): Map<string, string> {
  switch (action.type) {
    case 'download-file-received':
    case 'download-blob-received': {
      const { path } = action;
      if (state.has(path)) {
        const newState = new Map(state);
        newState.delete(path);
        return newState;
      }
      return state;
    }
    case 'download-file-error': {
      const { error, path } = action;
      const newState = new Map(state);
      newState.set(path, error);
      return newState;
    }
    case 'change-file-system':
    case 'remove-dropbox-oauth':
    case 'remove-browser-files':
    case 'remove-all-storage':
      return new Map();
    default:
      return state;
  }
}

function downloadFileCache(
  state: T.DownloadFileCache = new Map(),
  action: T.Action,
): T.DownloadFileCache {
  switch (action.type) {
    case 'move-file-done': {
      const { oldPath, metadata } = action;
      const file = state.get(oldPath);
      if (file && metadata.type === 'file') {
        // Update the metadata.
        const newState = new Map(state);
        newState.delete(oldPath);
        newState.set(metadata.path, {
          metadata,
          text: file.text,
        });
        return newState;
      }
      if (metadata.type === 'folder') {
        const newState: T.DownloadFileCache = new Map();
        for (const [path, value] of state) {
          if (path.startsWith(oldPath + '/') || path === oldPath) {
            const newPath = updatePathRoot(path, oldPath, metadata.path);
            newState.set(newPath, {
              ...value,
              metadata: {
                ...value.metadata,
                path: newPath,
              },
            });
          } else {
            newState.set(path, value);
          }
        }
        return newState;
      }
      return state;
    }
    case 'delete-file-done': {
      const newState = new Map(state);
      const { metadata } = action;

      newState.delete(metadata.path);

      if (metadata.type === 'folder') {
        for (const [path] of state) {
          if (path.startsWith(metadata.path + '/')) {
            newState.delete(path);
          }
        }
      }

      return newState;
    }
    case 'download-file-received': {
      const newState = new Map(state);
      const { path, file } = action;
      newState.set(path, file);
      return newState;
    }
    case 'change-file-system':
    case 'clear-api-cache':
    case 'remove-dropbox-oauth':
    case 'remove-browser-files':
    case 'remove-all-storage':
      return new Map();
    case 'invalidate-path':
      if (state.has(action.path)) {
        const newState = new Map(state);
        newState.delete(action.path);
        return newState;
      }
      return state;
    default:
      return state;
  }
}

function downloadBlobCache(
  state: T.DownloadBlobCache = new Map(),
  action: T.Action,
): T.DownloadBlobCache {
  switch (action.type) {
    case 'move-file-done': {
      const { oldPath, metadata } = action;
      const file = state.get(oldPath);
      if (file && metadata.type === 'file') {
        // Update the metadata.
        state.set(metadata.path, {
          metadata,
          blob: file.blob,
        });
      }
      return state;
    }
    case 'download-blob-received': {
      const newState = new Map(state);
      const { path, blobFile } = action;
      newState.set(path, blobFile);
      return newState;
    }
    case 'change-file-system':
    case 'clear-api-cache':
    case 'remove-dropbox-oauth':
    case 'remove-browser-files':
    case 'remove-all-storage':
      return new Map();
    default:
      return state;
  }
}

function path(state = '/', action: T.Action): string {
  switch (action.type) {
    case 'change-active-file':
    case 'view-list-files':
    case 'view-music':
    case 'view-language-coach':
    case 'view-file':
    case 'view-pdf':
    case 'view-image':
    case 'view-markdown':
      return action.path;
    case 'change-file-system':
      return '/';
    default:
      return state;
  }
}

function modifiedTextByPath(
  state = new Map(),
  action: T.Action,
): Map<string, { text: string | null; generation: number; path: string }> {
  switch (action.type) {
    case 'modify-active-file': {
      const path = action.path;
      const newState = new Map(state);
      const oldModifiedText = state.get(path);
      const generation = oldModifiedText?.generation ?? 0;
      newState.set(path, {
        text: action.modifiedText,
        // The textarea needs a signal that the text has been modified by something
        // external, so that it can update the textarea.value.
        generation: action.forceRefresh ? generation + 1 : generation,
      });
      return newState;
    }
    case 'download-file-received': {
      if (!state.has(action.path)) {
        return state;
      }
      const newState = new Map(state);
      newState.delete(action.path);
      return newState;
    }
    case 'move-file-done': {
      // Rather than rewriting the buffer to live at the new path, just drop
      // it. It's keyed by the now-stale old path, and it's simpler to let it
      // be re-derived than to keep it in sync with every kind of move.
      const { oldPath, metadata } = action;
      if (metadata.type === 'folder') {
        let changed = false;
        const newState = new Map(state);
        for (const path of state.keys()) {
          if (path === oldPath || path.startsWith(oldPath + '/')) {
            newState.delete(path);
            changed = true;
          }
        }
        return changed ? newState : state;
      }
      if (!state.has(oldPath)) {
        return state;
      }
      const newState = new Map(state);
      newState.delete(oldPath);
      return newState;
    }
    default:
      return state;
  }
}

function view(state: T.View | null = null, action: T.Action): T.View | null {
  switch (action.type) {
    case 'change-active-file':
      return 'view-file';
    case 'change-file-system':
    case 'view-list-files':
      return 'list-files';
    case 'view-music':
      return 'music';
    case 'view-file':
      return 'view-file';
    case 'view-language-coach':
      return 'language-coach';
    case 'view-pdf':
      return 'view-pdf';
    case 'view-image':
      return 'view-image';
    case 'view-markdown':
      return 'view-markdown';
    case 'view-settings':
      return 'settings';
    case 'view-connect':
      return 'connect';
    case 'view-connect-folder':
      return 'connect-folder';
    default:
      return state;
  }
}

function hideEditor(
  state: boolean = persistedState.appHideEditor.read() ?? true,
  action: T.Action,
): boolean {
  switch (action.type) {
    case 'hide-editor':
      persistedState.appHideEditor.write(action.flag);
      return action.flag;
    case 'set-editor-only':
      if (action.isEditorOnly) {
        persistedState.appHideEditor.write(false);
        return false;
      }
      return state;
    default:
      return state;
  }
}

function getDefaultEditorOnly() {
  const value = persistedState.appEditorOnly.read();
  if (value === null) {
    return window.innerWidth <= 500;
  }
  return value;
}

function editorOnly(
  state: boolean = getDefaultEditorOnly(),
  action: T.Action,
): boolean {
  switch (action.type) {
    case 'set-editor-only':
      return action.isEditorOnly;
    case 'hide-editor':
      if (action.flag) {
        persistedState.appEditorOnly.write(false);
        return false;
      }
      return state;
    default:
      return state;
  }
}

function messages(state: T.Message[] = [], action: T.Action): T.Message[] {
  switch (action.type) {
    case 'add-message': {
      const added: T.Message = {
        message: action.message,
        generation: action.generation,
        progress: action.progress,
      };
      return state.some((message) => message.generation === action.generation)
        ? state.map((message) =>
            message.generation === action.generation ? added : message,
          )
        : [...state, added];
    }
    case 'dismiss-message':
      return state.filter(
        (message) => message.generation !== action.generation,
      );
    case 'dismiss-all-messages':
      return [];
    default:
      return state;
  }
}

function isDraggingSplitter(state = false, action: T.Action): boolean {
  if (action.type === 'dragging-splitter') {
    return action.isDragging;
  }
  return state;
}

function shouldHideHeader(state: boolean = false, action: T.Action): boolean {
  switch (action.type) {
    case 'should-hide-header':
      return action.hide;
    default:
      return state;
  }
}

/**
 * Remember the previously viewed file system name.
 */
function getSavedFSName() {
  return toFileStoreName(persistedState.fileStoreName.read()) ?? 'browser';
}

function currentFileStoreName(
  state: T.FileStoreName = getSavedFSName(),
  action: T.Action,
): T.FileStoreName {
  switch (action.type) {
    case 'change-file-system': {
      const { fileStoreName } = action;
      persistedState.fileStoreName.write(fileStoreName);
      return fileStoreName;
    }
    case 'view-list-files':
      return action.fileStoreName;
    case 'view-music':
      persistedState.fileStoreName.write('server');
      return 'server';
    case 'remove-dropbox-oauth':
      if (state === 'dropbox') {
        persistedState.fileStoreName.write('browser');
        return 'browser';
      }
      return state;
    case 'remove-all-storage':
      return 'browser';
    default:
      return state;
  }
}

function renameFile(
  state: T.RenameFileState = { phase: 'none', path: null },
  action: T.Action,
): T.RenameFileState {
  switch (action.type) {
    case 'start-rename-file':
      return { phase: 'editing', path: action.path };
    case 'move-file-requested':
      // Only transition into "sending" if this move is for the file
      // currently being edited. `moveFile` is also used for batch renames of
      // other selected files, drag-and-drop, and cut/paste, none of which
      // should hijack the rename UI for an unrelated row.
      return state.path === action.path
        ? { phase: 'sending', path: action.path }
        : state;
    case 'stop-rename-file':
      return { phase: 'none', path: null };
    case 'move-file-done':
      return state.path === action.oldPath
        ? { phase: 'none', path: null }
        : state;
    default:
      return state;
  }
}

function filesIndex(
  state: FilesIndex | null = null,
  action: T.Action,
): FilesIndex | null {
  switch (action.type) {
    case 'files-index-received':
      return action.filesIndex;
    default:
      return state;
  }
}

function searchString(state = '', action: T.Action) {
  switch (action.type) {
    case 'set-search-string':
      return action.search;
    default:
      return state;
  }
}

function idbfs(
  state: FileStoreCache | null = null,
  action: T.Action,
): FileStoreCache | null {
  switch (action.type) {
    case 'connect-idbfs':
      return action.idbfs;
    case 'remove-browser-files':
    case 'remove-all-storage':
      return null;
    default:
      return state;
  }
}

function workerClient(
  state: WorkerClient | null = null,
  action: T.Action,
): WorkerClient | null {
  switch (action.type) {
    case 'set-worker-client':
      return action.workerClient;
    default:
      return state;
  }
}

function openAIApiKey(
  state: string | null = persistedState.openAIAPIKey.read(),
  action: T.Action,
): string | null {
  switch (action.type) {
    case 'set-open-ai-api-key':
      return action.apiKey;
    default:
      return state;
  }
}

function hasOnboarded(
  state: boolean = persistedState.hasOnboarded.read() ?? false,
  action: T.Action,
): boolean {
  switch (action.type) {
    case 'set-has-onboarded':
      persistedState.hasOnboarded.write(action.value);
      return action.value;
    case 'remove-all-storage':
      return false;
    default:
      return state;
  }
}

function getEditorAutocompleteDefaults(): {
  markdown: boolean;
  chordpro: boolean;
} {
  return persistedState.editorAutocompleteSettings.read();
}

function experimentalFeatures(
  state: boolean = persistedState.experimentalFeatures.read() ?? false,
  action: T.Action,
): boolean {
  switch (action.type) {
    case 'set-experimental-features':
      persistedState.experimentalFeatures.write(action.value);
      return action.value;
    default:
      return state;
  }
}

function getInputModeDefault(): T.InputMode {
  const raw = persistedState.inputMode.read();
  return raw === 'touch' || raw === 'mouse' ? raw : 'auto';
}

function inputMode(
  state: T.InputMode = getInputModeDefault(),
  action: T.Action,
): T.InputMode {
  switch (action.type) {
    case 'set-input-mode':
      persistedState.inputMode.write(action.value);
      return action.value;
    default:
      return state;
  }
}

function getDetectedPointerTypeDefault(): T.ResolvedInputMode {
  const raw = persistedState.detectedPointerType.read();
  if (raw === 'touch' || raw === 'mouse') {
    return raw;
  }
  // jsdom (tests) has no matchMedia; fall back to "mouse" there.
  if (typeof window.matchMedia !== 'function') {
    return 'mouse';
  }
  return window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'mouse';
}

function detectedPointerType(
  state: T.ResolvedInputMode = getDetectedPointerTypeDefault(),
  action: T.Action,
): T.ResolvedInputMode {
  switch (action.type) {
    case 'set-detected-pointer-type':
      persistedState.detectedPointerType.write(action.value);
      return action.value;
    default:
      return state;
  }
}

function fileStoreCacheEnabled(
  state: boolean = getFileStoreCacheEnabledDefault(),
  action: T.Action,
): boolean {
  switch (action.type) {
    case 'set-file-store-cache-enabled':
      persistedState.fileStoreCacheEnabled.write(action.value);
      return action.value;
    default:
      return state;
  }
}

function getFileStoreCacheEnabledDefault(): boolean {
  if (process.env.NODE_ENV === 'test') {
    return false;
  }
  return persistedState.fileStoreCacheEnabled.read() ?? true;
}

function editorAutocompleteSettings(
  state: {
    markdown: boolean;
    chordpro: boolean;
  } = getEditorAutocompleteDefaults(),
  action: T.Action,
): { markdown: boolean; chordpro: boolean } {
  switch (action.type) {
    case 'set-editor-autocomplete': {
      const nextState = {
        ...state,
        [action.editor]: action.value,
      };
      persistedState.editorAutocompleteSettings.write(nextState);
      return nextState;
    }
    default:
      return state;
  }
}

function musicImportQuality(
  state: ImportQualitySettings = persistedState.musicImportQuality.read(),
  action: T.Action,
): ImportQualitySettings {
  switch (action.type) {
    case 'set-music-import-quality':
      persistedState.musicImportQuality.write(action.value);
      return action.value;
    default:
      return state;
  }
}

function copyFile(
  state: T.CopyFileState | null = null,
  action: T.Action,
): T.CopyFileState | null {
  switch (action.type) {
    case 'set-copy-file':
      return { paths: action.paths, isCut: action.isCut };
    case 'clear-copy-file':
      return null;
    default:
      return state;
  }
}

function draggedFiles(
  state: string[] | null = null,
  action: T.Action,
): string[] | null {
  switch (action.type) {
    case 'set-dragged-files':
      return action.paths;
    case 'clear-dragged-files':
      return null;
    default:
      return state;
  }
}

/**
 * Drops folder-keyed state made stale by a file move or rename.
 */
function dropStaleFolderKeys<Value>(
  state: Record<string, Value>,
  oldPath: string,
  metadata: T.FileMetadata | T.FolderMetadata,
): Record<string, Value> {
  const newState = { ...state };
  delete newState[getDirName(oldPath)];
  if (metadata.type === 'folder') {
    for (const key of Object.keys(newState)) {
      if (key === oldPath || key.startsWith(oldPath + '/')) {
        delete newState[key];
      }
    }
  }
  return newState;
}

/**
 * Record<string, string> is the map of the folder to the file name. This way
 * the file focus is retained when navigating between folders.
 */
function fileFocusByPath(
  state: Record<string, string> = {},
  action: T.Action,
): Record<string, string> {
  switch (action.type) {
    case 'delete-file-done':
    case 'change-file-focus':
      if (!action.fileFocus) {
        const newState = { ...state };
        delete newState[action.folder];
        return newState;
      }
      return {
        ...state,
        [action.folder]: action.fileFocus,
      };
    case 'move-file-done':
      return dropStaleFolderKeys(state, action.oldPath, action.metadata);
    case 'change-file-system':
    case 'clear-api-cache':
    case 'remove-dropbox-oauth':
    case 'remove-browser-files':
    case 'remove-all-storage':
      return {};
    default:
      return state;
  }
}

/**
 * Record<string, string[]> is the map of the folder to the names of the
 * files selected within it, e.g. { '/Artist': ['song.mp3', 'song2.mp3'] }.
 * This tracks multi-select, alongside fileFocusByPath which tracks the
 * single keyboard/menu cursor position (e.g. { '/Artist': 'song.mp3' }).
 */
function fileSelectionByPath(
  state: Record<string, string[]> = {},
  action: T.Action,
): Record<string, string[]> {
  switch (action.type) {
    case 'set-file-selection':
      if (action.paths.length === 0) {
        const newState = { ...state };
        delete newState[action.folder];
        return newState;
      }
      return {
        ...state,
        [action.folder]: action.paths,
      };
    case 'delete-file-done': {
      const newState = { ...state };
      delete newState[action.folder];
      return newState;
    }
    case 'move-file-done':
      return dropStaleFolderKeys(state, action.oldPath, action.metadata);
    case 'change-file-system':
    case 'clear-api-cache':
    case 'remove-dropbox-oauth':
    case 'remove-browser-files':
    case 'remove-all-storage':
      return {};
    default:
      return state;
  }
}

export const reducers = combineReducers({
  currentFileStoreName,
  downloadBlobCache,
  downloadFileCache,
  downloadFileErrors,
  dropboxOauth,
  copyFile,
  draggedFiles,
  editorAutocompleteSettings,
  editorOnly,
  detectedPointerType,
  experimentalFeatures,
  fileStoreCacheEnabled,
  filesIndex,
  inputMode,
  serverId,
  servers,
  hideEditor,
  idbfs,
  isDraggingSplitter,
  languageCoach: languageCoachReducer,
  music: musicReducer,
  musicImportQuality,
  listFileErrors,
  listFilesCache,
  messages,
  modifiedTextByPath,
  hasOnboarded,
  path,
  renameFile,
  searchString,
  shouldHideHeader,
  view,
  openAIApiKey,
  workerClient,
  fileFocusByPath,
  fileSelectionByPath,
});

function wrapReducer<S>(
  reducer: T.Reducer<S>,
  fn: (state: S, action: T.Action) => S,
): T.Reducer<S> {
  return (state, action) => {
    const nextState = reducer(state, action);
    return fn(nextState, action);
  };
}

export type State = ReturnType<typeof reducers>;

export const mainReducer: T.Reducer<State> = wrapReducer(
  reducers,
  (state, action) => {
    state.filesIndex?.reducer(state, action);
    return state;
  },
);
